/**
 * 블로그 API (Phase 5-2 — DB 기반).
 *
 * 영구지침: docs/develop/seo/blog-cms-spec.md §4
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import apiClient from '@/lib/api/client'
import type { BlogCategory } from '@/lib/blog/types'

// =========================================================================
// 타입
// =========================================================================

export type BlogPostStatus = 'DRAFT' | 'SCHEDULED' | 'PUBLISHED' | 'ARCHIVED'

export interface BlogCta {
  text: string
  link: string
  emoji?: string
}

export interface BlogPost {
  id: number
  slug: string
  title: string
  category: BlogCategory
  summary: string
  content: string
  thumbnailUrl: string | null
  keywords: string[] | null
  ctaPrimary: BlogCta | null
  ctaSecondary: BlogCta | null
  author: string
  status: BlogPostStatus
  publishedAt: string | null
  scheduledAt: string | null
  viewCount: number
  autoGenerated: boolean
  createdAt: string
  updatedAt: string
}

export interface BlogPostRequest {
  slug: string
  title: string
  category: BlogCategory
  summary: string
  content: string
  thumbnailUrl?: string
  keywords?: string[]
  ctaPrimary?: BlogCta | null
  ctaSecondary?: BlogCta | null
  author?: string
  status?: BlogPostStatus
  scheduledAt?: string | null
}

interface SpringPage<T> {
  content: T[]
  totalElements: number
  totalPages: number
  number: number
  size: number
}

// =========================================================================
// 공개 API
// =========================================================================

/** 공개 블로그 목록 필터 */
export interface BlogPostFilter {
  category?: BlogCategory
  page?: number
  size?: number
}

/** 공개 블로그 목록 쿼리 키 — SSR 프리페치(entry-server.tsx)와 공유 */
export function blogPostsQueryKey(filter: BlogPostFilter) {
  return ['blog-posts', filter] as const
}

export function useBlogPosts(filter: BlogPostFilter) {
  return useQuery({
    queryKey: blogPostsQueryKey(filter),
    queryFn: async () => {
      const params: Record<string, string> = {
        page: String(filter.page ?? 0),
        size: String(filter.size ?? 12),
      }
      if (filter.category) params.category = filter.category
      const res = await apiClient.get('/blog/posts', { params })
      return res.data.data as SpringPage<BlogPost>
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useBlogPost(slug: string | undefined) {
  return useQuery({
    queryKey: ['blog-post', slug],
    queryFn: async () => {
      const res = await apiClient.get(`/blog/posts/${slug}`)
      // null 은 '글 미존재 확정' 신호 — SSR 프리페치(entry-server.tsx)가 백엔드 404 를 받으면
      // null 을 시드하고, BlogDetailPage 가 NotFoundPage(404) 를 렌더한다 (ssr-spec §15.2).
      return (res.data.data ?? null) as BlogPost | null
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  })
}

// =========================================================================
// 어드민 API
// =========================================================================

export function useAdminBlogPosts(filter: {
  category?: BlogCategory
  status?: BlogPostStatus | 'all'
  page?: number
  size?: number
}) {
  return useQuery({
    queryKey: ['admin-blog-posts', filter],
    queryFn: async () => {
      const params: Record<string, string> = {
        page: String(filter.page ?? 0),
        size: String(filter.size ?? 20),
      }
      if (filter.category) params.category = filter.category
      if (filter.status && filter.status !== 'all') params.status = filter.status
      const res = await apiClient.get('/admin/blog/posts', { params })
      return res.data.data as SpringPage<BlogPost>
    },
  })
}

export function useAdminBlogPost(id: number | undefined) {
  return useQuery({
    queryKey: ['admin-blog-post', id],
    queryFn: async () => {
      const res = await apiClient.get(`/admin/blog/posts/${id}`)
      return res.data.data as BlogPost
    },
    enabled: !!id,
  })
}

export function useCreateBlogPost() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: BlogPostRequest) => {
      const res = await apiClient.post('/admin/blog/posts', data)
      return res.data.data as BlogPost
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-blog-posts'] })
      qc.invalidateQueries({ queryKey: ['blog-posts'] })
    },
  })
}

export function useUpdateBlogPost() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: BlogPostRequest }) => {
      const res = await apiClient.patch(`/admin/blog/posts/${id}`, data)
      return res.data.data as BlogPost
    },
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['admin-blog-posts'] })
      qc.invalidateQueries({ queryKey: ['admin-blog-post', id] })
      qc.invalidateQueries({ queryKey: ['blog-posts'] })
    },
  })
}

export function useDeleteBlogPost() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/admin/blog/posts/${id}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-blog-posts'] })
      qc.invalidateQueries({ queryKey: ['blog-posts'] })
    },
  })
}

export function usePublishBlogPost() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await apiClient.post(`/admin/blog/posts/${id}/publish`)
      return res.data.data as BlogPost
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-blog-posts'] })
      qc.invalidateQueries({ queryKey: ['blog-posts'] })
    },
  })
}

export function useAutoGenerateBlogPost() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.post('/admin/blog/auto-generate')
      return res.data.data as { postId: number; title: string; slug: string }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-blog-posts'] })
    },
  })
}

export function useUnpublishBlogPost() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await apiClient.post(`/admin/blog/posts/${id}/unpublish`)
      return res.data.data as BlogPost
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-blog-posts'] })
      qc.invalidateQueries({ queryKey: ['blog-posts'] })
    },
  })
}
